import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCcw, Search } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/shared/ui/select';
import {
  getJournal, getSousComptes, LIBELLES_ACTION, LIBELLES_CIBLE, NIVEAUX,
} from '@/shared/api/superadmin';

const dateCourte = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' }); }
  catch { return iso; }
};

const TEINTE = {
  'tenant.create': 'bg-emerald-100 text-emerald-700',
  'tenant.reactivate': 'bg-emerald-100 text-emerald-700',
  'tenant.suspend': 'bg-red-100 text-red-700',
  'tenant.delete': 'bg-red-100 text-red-700',
  'tenant.admin_create': 'bg-blue-100 text-blue-700',
  'tenant.rename': 'bg-slate-100 text-slate-700',
  'tenant.observation': 'bg-amber-100 text-amber-700',
  'user.create': 'bg-blue-100 text-blue-700',
  'user.update': 'bg-slate-100 text-slate-700',
  'user.reset_password': 'bg-amber-100 text-amber-700',
  'user.delete': 'bg-red-100 text-red-700',
  'auth.login': 'bg-slate-100 text-slate-700',
  'auth.login_refuse': 'bg-amber-100 text-amber-700',
};

// La valeur vide vaut « tous » : `Select` de Radix n'accepte pas d'option de valeur ''.
const TOUS = 'tous';

export default function JournalPage() {
  const [lignes, setLignes] = useState([]);
  const [sousComptes, setSousComptes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [filtres, setFiltres] = useState({
    action: TOUS, cibleId: TOUS, niveau: TOUS, recherche: '',
  });

  const filtre = (valeur) => (valeur === TOUS ? '' : valeur);
  const filtreActif = filtres.action !== TOUS || filtres.cibleId !== TOUS
    || filtres.niveau !== TOUS || filtres.recherche.trim() !== '';

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      setLignes(await getJournal({
        action: filtre(filtres.action),
        cibleId: filtre(filtres.cibleId),
        niveau: filtre(filtres.niveau),
        recherche: filtres.recherche.trim(),
      }));
    } catch (e) { console.error(e); }
    setChargement(false);
  }, [filtres]);

  // La liste des sous-comptes ne dépend pas des filtres : une seule fois.
  useEffect(() => {
    getSousComptes().then(setSousComptes).catch((e) => console.error(e));
  }, []);

  // Chaque frappe ne part pas au serveur : sans ce délai, filtrer par e-mail
  // déclenche une requête par caractère.
  useEffect(() => {
    const minuteur = setTimeout(charger, filtres.recherche ? 300 : 0);
    return () => clearTimeout(minuteur);
  }, [charger, filtres.recherche]);

  const actionsListees = useMemo(() => Object.keys(LIBELLES_ACTION), []);

  const majFiltre = (champ) => (valeur) => setFiltres((f) => ({ ...f, [champ]: valeur }));

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="journal-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Journal</h1>
        </div>
        <Button variant="outline" size="sm" onClick={charger} data-testid="journal-rafraichir">
          <RefreshCcw className="mr-1 h-3.5 w-3.5" /> Rafraîchir
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-9 pl-8"
            placeholder="Rechercher un auteur ou une cible…"
            value={filtres.recherche}
            onChange={(e) => majFiltre('recherche')(e.target.value)}
            data-testid="journal-recherche"
          />
        </div>
        <Select value={filtres.action} onValueChange={majFiltre('action')}>
          <SelectTrigger className="h-9" data-testid="journal-filtre-action"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={TOUS}>Toutes les actions</SelectItem>
            {actionsListees.map((a) => (
              <SelectItem key={a} value={a}>{LIBELLES_ACTION[a]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filtres.cibleId} onValueChange={majFiltre('cibleId')}>
          <SelectTrigger className="h-9" data-testid="journal-filtre-sous-compte"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={TOUS}>Tous les sous-comptes visés</SelectItem>
            {sousComptes.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filtres.niveau} onValueChange={majFiltre('niveau')}>
          <SelectTrigger className="h-9" data-testid="journal-filtre-niveau"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={TOUS}>Tous les niveaux</SelectItem>
            {Object.entries(NIVEAUX).map(([cle, n]) => (
              <SelectItem key={cle} value={cle}>{n.libelle}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <p className="text-[12px] text-slate-500" data-testid="journal-compteur">
        {chargement
          ? 'Chargement…'
          : `${lignes.length} évènement${lignes.length > 1 ? 's' : ''}${filtreActif ? ' (filtrés)' : ''}`}
      </p>

      {chargement ? null : lignes.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="journal-vide">
          {filtreActif
            ? 'Aucun évènement pour ce filtre.'
            : 'Aucune action enregistrée pour l’instant.'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Quand</TableHead>
                <TableHead>Niveau</TableHead>
                <TableHead>Action</TableHead>
                {/* « Sous-compte » était faux : sur une action de compte, cette colonne
                    affiche un e-mail d'utilisateur. La cible porte donc son type. */}
                <TableHead>Cible</TableHead>
                <TableHead>Par</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lignes.map((l) => {
                const niveau = NIVEAUX[l.niveau] || NIVEAUX.info;
                return (
                  <TableRow key={l.id} data-testid={`journal-ligne-${l.id}`}>
                    <TableCell className="whitespace-nowrap text-[12px] text-slate-500">
                      {dateCourte(l.created_at)}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${niveau.teinte} hover:bg-inherit`} data-testid={`journal-niveau-${l.id}`}>
                        {niveau.libelle}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${TEINTE[l.action] || 'bg-slate-100 text-slate-700'} hover:bg-inherit`}>
                        {LIBELLES_ACTION[l.action] || l.action}
                      </Badge>
                    </TableCell>
                    <TableCell data-testid={`journal-cible-${l.id}`}>
                      <p className="font-medium text-slate-900">{l.cible_nom || '—'}</p>
                      {l.cible_type && (
                        <p className="text-[11px] text-slate-400">
                          {LIBELLES_CIBLE[l.cible_type] || l.cible_type}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-[12px] text-slate-600">
                      <p>{l.acteur_nom || '—'}</p>
                      {l.acteur_email && (
                        <p className="text-[11px] text-slate-400">{l.acteur_email}</p>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
